import { useAtomValue, useSetAtom } from "jotai";
import type { FunctionComponent } from "react";
import { useEffect, useRef, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { checkIsPostiesArticle } from "~/components/article/article-render/posties/helpers";
import { useArticleSpeechContext } from "~/components/article/article-speech/contexts";
import { useFlag } from "~/lib/feature-flag";
import { onlyOnePlayingPlayerAtom } from "~/lib/only-one-playing-player";
import { rosettaAtom } from "~/lib/rosetta";
import type { articleSpeechPlayerArticle$key } from "~/queries/__generated__/articleSpeechPlayerArticle.graphql";

import { useArticleSpeechPlayer, useBuildSpeechifyConfig } from "./hooks";
import { InlinePlayerContainer } from "./styles";

type Props = {
  articleElementReference: HTMLElement | null;
  reference: articleSpeechPlayerArticle$key;
};

export const ArticleSpeechPlayer: FunctionComponent<Props> = ({
  articleElementReference,
  reference: reference_,
}) => {
  const article = useFragment(
    graphql`
      fragment articleSpeechPlayerArticle on Article {
        ...helpersCheckIsPostiesArticle
        ...hooksBuildSpeechifyConfigArticle
        entityId
      }
    `,
    reference_,
  );

  const [isMounted, setIsMounted] = useState(false);
  const { articleSpeechifyPlayer, setArticleSpeechifyPlayer } = useArticleSpeechContext();

  const asyncRosettaState = useAtomValue(rosettaAtom);
  const dispatch = useSetAtom(onlyOnePlayingPlayerAtom);
  useArticleSpeechPlayer({ articleSpeechifyPlayer });

  const isPostiesArticle = checkIsPostiesArticle(article);
  const isSCMPArticleSpeechEnabled = useFlag("enable_scmp_article_speech");

  const inlinePlayerReference = useRef<HTMLDivElement>(null);
  const inlinePLayerId = `article-${article.entityId}`;

  const { config: speechifyConfig } = useBuildSpeechifyConfig({
    contentParentElement: articleElementReference,
    inlinePLayerId,
    inlinePlayerReference,
    reference: article,
  });

  useEffect(() => {
    if (
      !articleElementReference ||
      isMounted ||
      !asyncRosettaState ||
      asyncRosettaState.status !== "success" ||
      !speechifyConfig
    )
      return;

    const shouldShowPaywall =
      asyncRosettaState.status === "success" && !asyncRosettaState.result?.hasPostiesAccessRight;

    const isArticleSpeechEnabledExcludingPostiesArticle =
      !isSCMPArticleSpeechEnabled && !isPostiesArticle;

    if (shouldShowPaywall || isArticleSpeechEnabledExcludingPostiesArticle) return;

    const run = async () => {
      const speechifyWidget = await import(
        /* webpackIgnore: true */ "https://storage.googleapis.com/speechify-api-cdn/speechifyapi.min.mjs"
      );
      const widget = speechifyWidget.makeSpeechifyExperience(speechifyConfig);

      await widget.mount();
      setArticleSpeechifyPlayer?.(widget);
      dispatch({
        payload: {
          articleSpeechifyPlayer: widget,
        },
        type: "loadArticleSpeechPlayer",
      });
    };
    setIsMounted(true);
    void run();
  }, [
    articleElementReference,
    asyncRosettaState,
    dispatch,
    isMounted,
    isPostiesArticle,
    isSCMPArticleSpeechEnabled,
    setArticleSpeechifyPlayer,
    speechifyConfig,
  ]);

  return isPostiesArticle ? null : (
    <InlinePlayerContainer id={inlinePLayerId} ref={inlinePlayerReference} />
  );
};
